import type { PropType } from "vue";
import { defineComponent } from "vue";
import Icon from "@/components/ui/icon/Icon";
import Tooltip from "@/components/ui/tooltip/Tooltip";
import { $t } from "@/services/i18n";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";

export type RatingDimension = "speed" | "intelligence" | "cost";

/**
 * Fixed metadata per rating dimension. Colour is the fast-scan cue: the user
 * learns "amber = speed, indigo = brains, green = cheap" once and never reads a
 * label again. Colours use the app's Radix scale (`*-9`) — plain Tailwind
 * palette classes (amber-500 …) are NOT generated by this project's theme.
 */
const DIMENSIONS: Record<RatingDimension, { icon: string; color: string }> = {
  speed: { icon: "lucide:zap", color: "text-amber-9" },
  intelligence: { icon: "lucide:brain", color: "text-indigo-9" },
  cost: { icon: "lucide:coins", color: "text-green-9" },
};

/** Resolve the dimension label with literal i18n keys (keeps `$t` type-safe). */
function labelFor(dimension: RatingDimension): string {
  if (dimension === "speed") return $t("agents:rating_speed");
  if (dimension === "intelligence") return $t("agents:rating_intelligence");
  return $t("agents:rating_cost");
}

/**
 * One rating dimension (speed / intelligence / cost) as a compact, colour-coded,
 * fixed-width chip: colour icon + a 0-5 value. Fixed width means the three chips
 * line up into scannable columns across rows and never get shoved by badges.
 *
 * Accessibility (WCAG 1.4.13): the numeric value is always in `aria-label` AND
 * visible text — never only inside the tooltip. The icon is decorative.
 */
export default defineComponent({
  name: "StarRating",
  props: {
    dimension: p.$string<RatingDimension>(),
    /** 0-5, rounded to 1 decimal. Null when there's no data for this dimension. */
    value: {
      type: Number as PropType<number | null>,
      default: null,
    },
    /** Extra explanation shown in the tooltip (raw numbers, context). */
    tooltip: p.string(),
  },
  setup(props, { attrs }) {
    return () => {
      const meta = DIMENSIONS[props.dimension];
      const label = labelFor(props.dimension);
      const hasValue = props.value != null;
      const display = hasValue ? props.value!.toFixed(1) : "—";
      const ariaLabel = hasValue
        ? `${label}: ${display} ${$t("agents:rating_out_of_five")}`
        : `${label}: ${$t("agents:rating_no_data")}`;

      // Icon strength tracks the score: a strong rating burns bright, a weak one
      // fades back. Shown-model stars cluster in the upper band, so we map the
      // USEFUL range (2★→5★) onto 40%→100% opacity. A flat 0-5 map squashes every
      // real score into ~70-100% and reads as uniform (the earlier bug).
      const OPACITY_MIN = 0.4;
      const OPACITY_LOW_ANCHOR = 2; // at/below this star the icon is at its faintest
      const iconOpacity = hasValue
        ? OPACITY_MIN +
          Math.min(1, Math.max(0, (props.value! - OPACITY_LOW_ANCHOR) / (5 - OPACITY_LOW_ANCHOR))) *
            (1 - OPACITY_MIN)
        : undefined;

      const chip = (
        <span
          aria-label={ariaLabel}
          class={cn("inline-flex w-11 items-center gap-1", attrs.class as string)}
        >
          <Icon
            icon={meta.icon}
            aria-hidden="true"
            style={hasValue ? { opacity: iconOpacity } : undefined}
            class={cn("h-3.5 w-3.5 flex-none", hasValue ? meta.color : "text-muted-foreground/30")}
          />
          <span
            aria-hidden="true"
            class={cn(
              "text-xs font-semibold tabular-nums",
              hasValue ? "text-foreground/85" : "text-muted-foreground/40"
            )}
          >
            {display}
          </span>
        </span>
      );

      if (!props.tooltip) return chip;

      return (
        <Tooltip text={props.tooltip} class="max-w-64 text-wrap">
          {chip}
        </Tooltip>
      );
    };
  },
});
